import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { EmptyStateComponent } from '../../../../components/empty-state/empty-state.component';
import { PaginationComponent } from '../../../../components/pagination/pagination.component';
import { SkeletonComponent } from '../../../../components/skeleton/skeleton.component';
import { UserModalComponent } from '../../../../components/user-modal/user-modal.component';
import { DeviceModel } from '../../../../models/device.model';
import { SessionModel } from '../../../../models/session.model';
import { DateToMomentPipe } from '../../../../pipes/date-to-moment.pipe';
import { ConfirmService } from '../../../../services/confirm.service';
import { SessionService } from '../../../../services/session.service';
import { ToastService } from '../../../../services/toast.service';
import { UserService } from '../../../../services/user.service';
import { UtilService } from '../../../../services/util.service';

/** Valor del filtro de sesiones: cadena vacia = "todas". */
type ActiveFilter = '' | 'true' | 'false';

const SESSIONS_PAGE_SIZE = 10;

/**
 * Detalle de usuario: datos de la cuenta, dispositivos y sesiones.
 *
 * `uid` llega por binding de parametros de ruta (withComponentInputBinding).
 */
@Component({
  selector: 'app-user-page',
  imports: [
    RouterLink,
    DateToMomentPipe,
    PaginationComponent,
    EmptyStateComponent,
    SkeletonComponent,
    FormsModule,
    NgSelectComponent,
  ],
  templateUrl: './user-page.component.html',
  styleUrl: './user-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPageComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly sessionService = inject(SessionService);
  private readonly confirmService = inject(ConfirmService);
  private readonly toastService = inject(ToastService);
  private readonly utilService = inject(UtilService);
  private readonly modal = inject(NgbModal);

  /** Parametro de ruta `/dashboard/users/:uid`. */
  readonly uid = input.required<string>();

  protected readonly user = this.userService.user;

  protected readonly devices = this.userService.devices;
  protected readonly loadingDevices = this.userService.loadingDevices;

  protected readonly sessions = this.userService.sessions;
  protected readonly loadingSessions = this.userService.loadingSessions;
  protected readonly sessionsTotal = this.userService.sessionsTotal;
  protected readonly sessionsPageSize = this.userService.sessionsPageSize;

  /**
   * `loaded` = la primera carga YA TERMINO, con exito o con error, asi que ya
   * se sabe si hay datos. `loading` no sirve para decidirlo: arranca en `false`
   * y solo se enciende cuando arranca la peticion, de modo que el primer render
   * pintaria los estados vacios antes de que la carga hubiera empezado.
   *
   * El de la ficha vive en el componente (el servicio es un singleton y su
   * bandera seguiria encendida al abrir OTRO usuario) y NO se reinicia en las
   * recargas: editar al usuario no debe borrar la ficha de la pantalla.
   */
  protected readonly userLoaded = signal(false);
  protected readonly devicesLoaded = this.userService.devicesLoaded;
  protected readonly sessionsLoaded = this.userService.sessionsLoaded;

  /**
   * Mensaje del fallo de carga, o `null`. Distingue «no se pudo cargar» de
   * «no hay nada»: no son lo mismo y el usuario necesita saber cual es.
   */
  protected readonly userError = this.userService.userError;
  protected readonly devicesError = this.userService.devicesError;
  protected readonly sessionsError = this.userService.sessionsError;

  protected readonly activeFilter = signal<ActiveFilter>('');
  protected readonly sessionsPage = signal(1);

  /**
   * Opciones fijas del filtro. "Todas las sesiones" es un item REAL de la
   * lista (no un placeholder): el ng-select se comporta identico al `<select>`
   * nativo que reemplaza.
   */
  protected readonly activeOptions: { value: ActiveFilter; label: string }[] = [
    { value: '', label: 'Todas las sesiones' },
    { value: 'true', label: 'Solo activas' },
    { value: 'false', label: 'Solo cerradas' },
  ];

  /** Marca temporal para confirmar la copia del uid al portapapeles. */
  protected readonly copied = signal(false);

  protected readonly initials = computed(() => {
    const user = this.user();
    if (!user) return '';
    const first = user.names.trim().charAt(0);
    const last = user.lastNames.trim().charAt(0);
    return `${first}${last}`.toUpperCase() || user.email.charAt(0).toUpperCase();
  });

  async ngOnInit(): Promise<void> {
    await this.loadUser();
    await Promise.all([this.loadDevices(), this.loadSessions()]);
  }

  protected async loadUser(): Promise<void> {
    try {
      await this.userService.getUser(this.uid());
    } finally {
      // Tambien tras un fallo: lo que apaga los esqueletos es «ya se intento»,
      // no «salio bien». El error se cuenta aparte, con `userError()`.
      this.userLoaded.set(true);
    }
  }

  protected async loadDevices(): Promise<void> {
    await this.userService.getDevices(this.uid());
  }

  protected async loadSessions(): Promise<void> {
    const active = this.activeFilter();
    await this.userService.getSessions(this.uid(), {
      active: active === '' ? undefined : active === 'true',
      page: this.sessionsPage(),
      pageSize: SESSIONS_PAGE_SIZE,
    });
  }

  /**
   * `null` llega cuando se limpia el ng-select con el boton de "x"; se
   * resuelve exactamente igual que elegir "Todas las sesiones".
   */
  protected onActiveFilter(value: ActiveFilter | null): void {
    this.activeFilter.set(value ?? '');
    this.sessionsPage.set(1);
    void this.loadSessions();
  }

  protected onSessionsPageChange(page: number): void {
    this.sessionsPage.set(page);
    void this.loadSessions();
  }

  /** Icono de Bootstrap Icons segun la plataforma del dispositivo. */
  protected platformIcon(device: DeviceModel): string {
    const platform = device.platform?.toLowerCase() ?? '';
    if (platform.includes('android')) return 'bi-android2';
    if (platform.includes('ios') || platform.includes('apple') || platform.includes('mac')) {
      return 'bi-apple';
    }
    return 'bi-phone';
  }

  protected async copyUid(): Promise<void> {
    const user = this.user();
    if (!user) return;

    const copied = await this.utilService.copyToClipboard(user.uid);
    if (copied) {
      this.copied.set(true);
      this.toastService.success('UID copiado al portapapeles.');
      setTimeout(() => this.copied.set(false), 2000);
      return;
    }

    this.toastService.error('No fue posible copiar el UID.');
  }

  protected async edit(): Promise<void> {
    const user = this.user();
    if (!user) return;

    const reference = this.modal.open(UserModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      ariaLabelledBy: 'ks-user-modal-title',
    });

    (reference.componentInstance as UserModalComponent).setUser(user);

    const updated = await reference.result.catch(() => null);
    if (updated) await this.loadUser();
  }

  /** Cerrar una sesion desconecta el dispositivo: se confirma en rojo. */
  protected async closeSession(session: SessionModel): Promise<void> {
    if (!session.active) return;

    const confirmed = await this.confirmService.ask({
      title: 'Cerrar sesión',
      message:
        'El dispositivo quedará desconectado de inmediato y tendrá que iniciar sesión de nuevo en la app móvil.',
      detail: `${session.deviceName || session.model} · ${session.location || session.ip}`,
      confirmLabel: 'Cerrar sesión',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;

    const closed = await this.sessionService.closeSession(session.sessionId);
    if (closed) await this.loadSessions();
  }
}
